/**
 * Country Dossier Panel — full-page overlay providing a comprehensive
 * intelligence brief for a selected country.
 *
 * Sections:
 *   1. CII Header — overall score, classification, trend arrow, sparkline
 *   2. CII Breakdown — 4 component bars (unrest, conflict, security, information)
 *   3. Event Timeline — chronological event list (last 50)
 *   4. Signals — aggregated intelligence signals for the country
 *   5. News Feed — recent news/RSS events filtered to the country
 *   6. Infrastructure — nearby assets via related-assets module (Session 2)
 *   7. Convergence Zones — any convergence cells touching the country
 *   8. Risk Assessment — composite risk score if available
 *
 * All data comes from existing analysis modules — no new data fetching.
 * Export button uses the export-dialog from Session 2.
 *
 * Opens via event bus: 'country:dossier' { name, code }
 * Triggered from the country panel's "Full Dossier" button.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { getCIIClassification, getCIIColor } from '@/intelligence/analysis/cii';
import { findRelatedAssets, ASSET_CATEGORY_LABELS } from '@/intelligence/analysis/related-assets';
import { createTrendSparkline } from '@/ui/components/sparkline';
import { createCompactEventItem } from '@/ui/components/compact-event-item';
import { filterEventsByTimeRange } from '@/utils/time-filter';
import { showExportDialog } from '@/ui/components/export-dialog';
import { relativeTime } from '@/utils/time';
import type { NormalizedEvent } from '@/sources/base-source';
import type {
  CIIScore,
  ConvergenceCell,
  AggregatedSignal,
  CompositeRisk,
} from '@/intelligence/analysis/types';
import type { AssetCategory } from '@/intelligence/analysis/related-assets';
import { panelManager } from '@/panels/panel-manager';

/** Max events to show in the timeline section. */
const MAX_TIMELINE_EVENTS = 50;
/** Max news items to show. */
const MAX_NEWS_ITEMS = 30;
/** Max CII history points for sparkline. */
const MAX_CII_HISTORY = 20;

export class CountryDossierPanel {
  private container: HTMLElement;
  private scrollArea: HTMLElement;
  private currentCountry: { name: string; code: string } | null = null;
  private cleanupFns: (() => void)[] = [];
  private renderPending = false;
  private ciiHistory: Map<string, number[]> = new Map();

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'country-dossier-panel';

    this.scrollArea = document.createElement('div');
    this.scrollArea.className = 'dossier-scroll';
    this.container.appendChild(this.scrollArea);

    this.renderEmpty();

    // Open dossier on country:dossier event
    this.cleanupFns.push(
      eventBus.on('country:dossier', (country) => {
        this.currentCountry = country;
        this.render(country);
        panelManager.showPanel('country-dossier');
      }),
    );

    // Re-render when analysis completes (new data)
    this.cleanupFns.push(
      eventBus.on('analysis:complete', (result) => {
        // Record CII history for sparklines
        if (result.ciiScores) {
          for (const [code, score] of result.ciiScores) {
            let history = this.ciiHistory.get(code);
            if (!history) {
              history = [];
              this.ciiHistory.set(code, history);
            }
            history.push(score.overall);
            if (history.length > MAX_CII_HISTORY) {
              history.shift();
            }
          }
        }
        this.scheduleRender();
      }),
    );

    // Re-render when time range changes
    this.cleanupFns.push(
      appStore.subscribe(
        (s) => s.timeRange,
        () => this.scheduleRender(),
      ),
    );
  }

  getElement(): HTMLElement {
    return this.container;
  }

  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
  }

  private scheduleRender(): void {
    if (!this.currentCountry || this.renderPending) return;
    this.renderPending = true;
    requestAnimationFrame(() => {
      this.renderPending = false;
      if (this.currentCountry) {
        this.render(this.currentCountry);
      }
    });
  }

  private renderEmpty(): void {
    this.scrollArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding: 48px 24px; text-align: center; color: var(--text-muted); font-size: 13px;';
    msg.textContent = 'Select a country to view its intelligence dossier';
    this.scrollArea.appendChild(msg);
  }

  private render(country: { name: string; code: string }): void {
    this.scrollArea.textContent = '';

    const engine = getAnalysisEngine();
    const lastResult = engine?.getLastResult();
    const state = appStore.getState();
    const allEvents = filterEventsByTimeRange(state.events, state.timeRange);
    const countryEvents = this.getCountryEvents(allEvents, country.code, country.name);
    const ciiScore = lastResult?.ciiScores.get(country.code.toUpperCase());

    // --- Dossier Header ---
    this.scrollArea.appendChild(this.buildDossierHeader(country, ciiScore, countryEvents.length));

    // --- Section: CII Breakdown ---
    this.scrollArea.appendChild(this.buildCIISection(country.code, ciiScore));

    // --- Section: Risk Assessment ---
    // regionName may be a conflict zone label (e.g. "Eastern Ukraine") rather
    // than the exact country name, so check for containment in both directions.
    const upperName = country.name.toUpperCase();
    const risk = lastResult?.compositeRisks.find((r) => {
      const rn = r.regionName.toUpperCase();
      return rn === upperName || rn.includes(upperName) || upperName.includes(rn);
    });
    if (risk) {
      this.scrollArea.appendChild(this.buildRiskSection(risk));
    }

    // --- Section: Signals ---
    const signal = lastResult?.aggregatedSignals.find(
      (s) => s.countryCode.toUpperCase() === country.code.toUpperCase(),
    );
    if (signal) {
      this.scrollArea.appendChild(this.buildSignalsSection(signal));
    }

    // --- Section: Convergence Zones ---
    const convergences = this.getCountryConvergences(
      lastResult?.convergences ?? [],
      countryEvents,
    );
    if (convergences.length > 0) {
      this.scrollArea.appendChild(this.buildConvergenceSection(convergences));
    }

    // --- Section: Infrastructure ---
    this.scrollArea.appendChild(this.buildInfrastructureSection(countryEvents));

    // --- Section: Event Timeline ---
    this.scrollArea.appendChild(this.buildTimelineSection(countryEvents));

    // --- Section: News Feed ---
    this.scrollArea.appendChild(this.buildNewsFeedSection(countryEvents));
  }

  // ─── Dossier Header ────────────────────────────────────────────────

  private buildDossierHeader(
    country: { name: string; code: string },
    ciiScore: CIIScore | undefined,
    eventCount: number,
  ): HTMLElement {
    const header = document.createElement('div');
    header.className = 'dossier-header';

    // Top row: country name + export button
    const topRow = document.createElement('div');
    topRow.className = 'dossier-header-top';

    const titleWrap = document.createElement('div');

    const countryName = document.createElement('h2');
    countryName.className = 'dossier-country-name';
    countryName.textContent = country.name;
    titleWrap.appendChild(countryName);

    const countryCode = document.createElement('span');
    countryCode.className = 'dossier-country-code';
    countryCode.textContent = country.code.toUpperCase();
    titleWrap.appendChild(countryCode);

    topRow.appendChild(titleWrap);

    const exportBtn = document.createElement('button');
    exportBtn.className = 'dossier-export-btn';
    exportBtn.textContent = 'Export';
    exportBtn.title = 'Export event data';
    exportBtn.onclick = () => showExportDialog();
    topRow.appendChild(exportBtn);

    header.appendChild(topRow);

    // Stats row: event count + CII badge
    const statsRow = document.createElement('div');
    statsRow.className = 'dossier-stats-row';

    const eventsBadge = document.createElement('span');
    eventsBadge.className = 'dossier-stat-badge';
    eventsBadge.textContent = `${eventCount} events`;
    statsRow.appendChild(eventsBadge);

    const timeRangeBadge = document.createElement('span');
    timeRangeBadge.className = 'dossier-stat-badge';
    timeRangeBadge.textContent = appStore.getState().timeRange === 'all'
      ? 'All time'
      : `Last ${appStore.getState().timeRange}`;
    statsRow.appendChild(timeRangeBadge);

    if (ciiScore) {
      const ciiBadge = document.createElement('span');
      ciiBadge.className = 'dossier-stat-badge';
      ciiBadge.style.background = `${getCIIColor(ciiScore.overall)}22`;
      ciiBadge.style.color = getCIIColor(ciiScore.overall);
      ciiBadge.style.borderColor = `${getCIIColor(ciiScore.overall)}44`;
      const classification = getCIIClassification(ciiScore.overall);
      ciiBadge.textContent = `CII ${ciiScore.overall} — ${classification.toUpperCase()}`;
      statsRow.appendChild(ciiBadge);
    }

    header.appendChild(statsRow);

    return header;
  }

  // ─── CII Section ───────────────────────────────────────────────────

  private buildCIISection(countryCode: string, ciiScore: CIIScore | undefined): HTMLElement {
    const section = this.createSection('Country Instability Index');

    if (!ciiScore) {
      const engine = getAnalysisEngine();
      const isWarmingUp = engine && !engine.isCIIActive();
      section.appendChild(this.createPlaceholder(
        isWarmingUp
          ? 'CII warming up (baseline collection in progress)'
          : 'CII not available for this country',
      ));
      return section;
    }

    const card = document.createElement('div');
    card.className = 'dossier-cii-card';

    // Score + classification + trend row
    const scoreRow = document.createElement('div');
    scoreRow.className = 'dossier-cii-score-row';

    const scoreNum = document.createElement('span');
    scoreNum.className = 'dossier-cii-score';
    scoreNum.style.color = getCIIColor(ciiScore.overall);
    scoreNum.textContent = String(ciiScore.overall);
    scoreRow.appendChild(scoreNum);

    const classification = getCIIClassification(ciiScore.overall);
    const classLabel = document.createElement('span');
    classLabel.className = 'dossier-cii-class';
    classLabel.style.background = `${getCIIColor(ciiScore.overall)}22`;
    classLabel.style.color = getCIIColor(ciiScore.overall);
    classLabel.textContent = classification.toUpperCase();
    scoreRow.appendChild(classLabel);

    // Trend arrow
    const trendArrow = document.createElement('span');
    trendArrow.className = 'dossier-cii-trend';
    if (ciiScore.trend === 'rising') {
      trendArrow.textContent = '\u2191 Rising';
      trendArrow.style.color = 'var(--accent-red)';
    } else if (ciiScore.trend === 'falling') {
      trendArrow.textContent = '\u2193 Falling';
      trendArrow.style.color = 'var(--accent-green)';
    } else {
      trendArrow.textContent = '\u2192 Stable';
      trendArrow.style.color = 'var(--text-muted)';
    }
    scoreRow.appendChild(trendArrow);

    card.appendChild(scoreRow);

    // Sparkline (trend history)
    const upperCode = countryCode.toUpperCase();
    const history = this.ciiHistory.get(upperCode);
    if (history && history.length >= 2) {
      const sparkRow = document.createElement('div');
      sparkRow.style.cssText = 'margin: 10px 0; display: flex; align-items: center; gap: 8px;';

      const sparkLabel = document.createElement('span');
      sparkLabel.style.cssText = 'font-size: 10px; color: var(--text-muted);';
      sparkLabel.textContent = 'Trend';
      sparkRow.appendChild(sparkLabel);

      const sparkline = createTrendSparkline(history, {
        width: 160,
        height: 24,
        fill: true,
        referenceLine: history[0],
      });
      sparkRow.appendChild(sparkline);

      card.appendChild(sparkRow);
    }

    // Component bars
    const components: [string, number, string][] = [
      ['Unrest', ciiScore.components.unrest, '#f97316'],
      ['Conflict', ciiScore.components.conflict, '#ef4444'],
      ['Security', ciiScore.components.security, '#8b5cf6'],
      ['Information', ciiScore.components.information, '#06b6d4'],
    ];

    for (const [label, value, color] of components) {
      card.appendChild(this.buildComponentBar(label, value, 25, color));
    }

    // Last updated
    const updated = document.createElement('div');
    updated.className = 'dossier-timestamp';
    const ts = new Date(ciiScore.lastUpdated);
    updated.textContent = `Updated ${ts.getUTCHours().toString().padStart(2, '0')}:${ts.getUTCMinutes().toString().padStart(2, '0')} UTC`;
    card.appendChild(updated);

    section.appendChild(card);
    return section;
  }

  // ─── Risk Assessment Section ───────────────────────────────────────

  private buildRiskSection(risk: CompositeRisk): HTMLElement {
    const section = this.createSection('Risk Assessment');

    const card = document.createElement('div');
    card.className = 'dossier-card';

    // Overall score
    const scoreRow = document.createElement('div');
    scoreRow.className = 'dossier-cii-score-row';

    const scoreNum = document.createElement('span');
    scoreNum.className = 'dossier-cii-score';
    scoreNum.style.color = risk.score >= 70 ? '#ef4444' : risk.score >= 40 ? '#f59e0b' : '#22c55e';
    scoreNum.textContent = String(risk.score);
    scoreRow.appendChild(scoreNum);

    const riskLabel = document.createElement('span');
    riskLabel.className = 'dossier-cii-class';
    const riskLevel = risk.score >= 70 ? 'HIGH RISK' : risk.score >= 40 ? 'MODERATE' : 'LOW RISK';
    const riskColor = risk.score >= 70 ? '#ef4444' : risk.score >= 40 ? '#f59e0b' : '#22c55e';
    riskLabel.style.background = `${riskColor}22`;
    riskLabel.style.color = riskColor;
    riskLabel.textContent = riskLevel;
    scoreRow.appendChild(riskLabel);

    card.appendChild(scoreRow);

    // Component bars
    card.appendChild(this.buildComponentBar('Convergence', risk.components.convergence, 100, '#06b6d4'));
    card.appendChild(this.buildComponentBar('CII', risk.components.cii, 100, '#f59e0b'));
    card.appendChild(this.buildComponentBar('Infrastructure', risk.components.infrastructure, 100, '#8b5cf6'));

    // Boosts
    if (risk.boosts.theaterPosture > 0 || risk.boosts.breakingNews > 0) {
      const boostRow = document.createElement('div');
      boostRow.style.cssText = 'display: flex; gap: 8px; margin-top: 8px;';

      if (risk.boosts.theaterPosture > 0) {
        const boost = document.createElement('span');
        boost.className = 'dossier-boost-badge';
        boost.textContent = `+${risk.boosts.theaterPosture} Theater`;
        boostRow.appendChild(boost);
      }
      if (risk.boosts.breakingNews > 0) {
        const boost = document.createElement('span');
        boost.className = 'dossier-boost-badge';
        boost.textContent = `+${risk.boosts.breakingNews} Breaking`;
        boostRow.appendChild(boost);
      }

      card.appendChild(boostRow);
    }

    section.appendChild(card);
    return section;
  }

  // ─── Signals Section ───────────────────────────────────────────────

  private buildSignalsSection(signal: AggregatedSignal): HTMLElement {
    const section = this.createSection('Intelligence Signals');

    if (signal.signals.length === 0) {
      section.appendChild(this.createPlaceholder('No active signals'));
      return section;
    }

    const grid = document.createElement('div');
    grid.className = 'dossier-signal-grid';

    for (const sig of signal.signals) {
      const chip = document.createElement('div');
      chip.className = 'dossier-signal-chip';

      const typeLabel = document.createElement('span');
      typeLabel.className = 'dossier-signal-type';
      typeLabel.textContent = sig.type.replace(/_/g, ' ');
      chip.appendChild(typeLabel);

      const countLabel = document.createElement('span');
      countLabel.className = 'dossier-signal-count';
      countLabel.textContent = String(sig.count);
      chip.appendChild(countLabel);

      grid.appendChild(chip);
    }

    if (signal.zone) {
      const zoneNote = document.createElement('div');
      zoneNote.style.cssText = 'margin-top: 8px; font-size: 11px; color: var(--accent);';
      zoneNote.textContent = `Convergence zone: ${signal.zone}`;
      grid.appendChild(zoneNote);
    }

    section.appendChild(grid);
    return section;
  }

  // ─── Convergence Zones Section ─────────────────────────────────────

  private buildConvergenceSection(convergences: ConvergenceCell[]): HTMLElement {
    const section = this.createSection(`Convergence Zones (${convergences.length})`);

    for (const cell of convergences) {
      const row = document.createElement('div');
      row.className = 'dossier-convergence-row';

      const label = document.createElement('span');
      label.className = 'dossier-convergence-label';
      label.textContent = cell.label || `${cell.lat.toFixed(1)}, ${cell.lng.toFixed(1)}`;
      row.appendChild(label);

      const typesWrap = document.createElement('div');
      typesWrap.className = 'dossier-convergence-types';
      for (const et of cell.eventTypes) {
        const chip = document.createElement('span');
        chip.className = 'dossier-type-chip';
        chip.textContent = et.replace(/_/g, ' ');
        typesWrap.appendChild(chip);
      }
      row.appendChild(typesWrap);

      const stats = document.createElement('span');
      stats.className = 'dossier-convergence-stats';
      stats.textContent = `${cell.totalEvents} events \u00B7 score ${cell.score}`;
      row.appendChild(stats);

      section.appendChild(row);
    }

    return section;
  }

  // ─── Infrastructure Section ────────────────────────────────────────

  private buildInfrastructureSection(countryEvents: NormalizedEvent[]): HTMLElement {
    const section = this.createSection('Nearby Infrastructure');

    // Use the centroid of country events as the query point
    const center = this.getEventsCentroid(countryEvents);
    if (!center) {
      section.appendChild(this.createPlaceholder('No geolocated events — cannot query infrastructure'));
      return section;
    }

    const assets = findRelatedAssets(center.lat, center.lng, 200);

    if (assets.length === 0) {
      section.appendChild(this.createPlaceholder('No infrastructure found within 200 km'));
      return section;
    }

    // Group by category
    const grouped = new Map<AssetCategory, typeof assets>();
    for (const asset of assets) {
      const list = grouped.get(asset.type) ?? [];
      list.push(asset);
      grouped.set(asset.type, list);
    }

    for (const [category, items] of grouped) {
      const catHeader = document.createElement('div');
      catHeader.className = 'dossier-infra-category';
      catHeader.textContent = `${ASSET_CATEGORY_LABELS[category]} (${items.length})`;
      section.appendChild(catHeader);

      for (const item of items.slice(0, 5)) {
        const row = document.createElement('div');
        row.className = 'dossier-infra-row';

        const name = document.createElement('span');
        name.className = 'dossier-infra-name';
        name.textContent = item.name;
        row.appendChild(name);

        const distance = document.createElement('span');
        distance.className = 'dossier-infra-distance';
        distance.textContent = `${item.distanceKm} km ${item.compass}`;
        row.appendChild(distance);

        section.appendChild(row);
      }

      if (items.length > 5) {
        const more = document.createElement('div');
        more.style.cssText = 'font-size: 10px; color: var(--text-muted); padding: 2px 0 6px 12px;';
        more.textContent = `+${items.length - 5} more`;
        section.appendChild(more);
      }
    }

    return section;
  }

  // ─── Event Timeline Section ────────────────────────────────────────

  private buildTimelineSection(countryEvents: NormalizedEvent[]): HTMLElement {
    const section = this.createSection(`Event Timeline (${countryEvents.length})`);

    if (countryEvents.length === 0) {
      section.appendChild(this.createPlaceholder('No events recorded'));
      return section;
    }

    // Type breakdown
    const typeCounts = new Map<string, number>();
    for (const ev of countryEvents) {
      const t = ev.type.replace(/_/g, ' ');
      typeCounts.set(t, (typeCounts.get(t) ?? 0) + 1);
    }

    const breakdown = document.createElement('div');
    breakdown.className = 'dossier-type-breakdown';

    const sorted = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1]);
    for (const [type, count] of sorted) {
      const chip = document.createElement('span');
      chip.className = 'dossier-type-chip';
      chip.textContent = `${type} (${count})`;
      breakdown.appendChild(chip);
    }

    section.appendChild(breakdown);

    // Chronological event list (newest first)
    const timeline = countryEvents
      .slice()
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, MAX_TIMELINE_EVENTS);

    const listEl = document.createElement('div');
    listEl.className = 'dossier-event-list';

    for (const ev of timeline) {
      listEl.appendChild(createCompactEventItem(ev));
    }

    if (countryEvents.length > MAX_TIMELINE_EVENTS) {
      const more = document.createElement('div');
      more.style.cssText = 'font-size: 10px; color: var(--text-muted); padding: 6px 0; text-align: center;';
      more.textContent = `+${countryEvents.length - MAX_TIMELINE_EVENTS} more events`;
      listEl.appendChild(more);
    }

    section.appendChild(listEl);
    return section;
  }

  // ─── News Feed Section ─────────────────────────────────────────────

  private buildNewsFeedSection(countryEvents: NormalizedEvent[]): HTMLElement {
    const section = this.createSection('News Feed');

    // Filter to news/RSS-type events
    const newsSources = new Set(['rss', 'gdelt']);
    const newsEvents = countryEvents
      .filter((e) => newsSources.has(e.source) || e.type === 'political' || e.type === 'diplomatic')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, MAX_NEWS_ITEMS);

    if (newsEvents.length === 0) {
      section.appendChild(this.createPlaceholder('No news events for this country'));
      return section;
    }

    const list = document.createElement('div');
    list.className = 'dossier-event-list';

    for (const ev of newsEvents) {
      const item = document.createElement('div');
      item.className = 'dossier-news-item';

      const titleRow = document.createElement('div');
      titleRow.className = 'dossier-news-title';

      const title = document.createElement('span');
      title.textContent = ev.title;
      titleRow.appendChild(title);

      const time = document.createElement('span');
      time.className = 'dossier-news-time';
      time.textContent = relativeTime(ev.timestamp);
      titleRow.appendChild(time);

      item.appendChild(titleRow);

      if (ev.description) {
        const desc = document.createElement('div');
        desc.className = 'dossier-news-desc';
        desc.textContent = ev.description.length > 200
          ? ev.description.slice(0, 200) + '...'
          : ev.description;
        item.appendChild(desc);
      }

      const source = document.createElement('div');
      source.className = 'dossier-news-source';
      source.textContent = ev.source;
      item.appendChild(source);

      // Click to select event
      item.style.cursor = 'pointer';
      item.onclick = () => eventBus.emit('event:selected', ev);

      list.appendChild(item);
    }

    section.appendChild(list);
    return section;
  }

  // ─── Helpers ───────────────────────────────────────────────────────

  private createSection(title: string): HTMLElement {
    const section = document.createElement('div');
    section.className = 'dossier-section';

    const header = document.createElement('div');
    header.className = 'dossier-section-header';
    header.textContent = title;
    section.appendChild(header);

    return section;
  }

  private createPlaceholder(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'dossier-placeholder';
    el.textContent = text;
    return el;
  }

  private buildComponentBar(label: string, value: number, max: number, color: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'dossier-component-bar';

    const labelEl = document.createElement('span');
    labelEl.className = 'dossier-bar-label';
    labelEl.textContent = label;
    row.appendChild(labelEl);

    const barBg = document.createElement('div');
    barBg.className = 'dossier-bar-bg';

    const barFill = document.createElement('div');
    barFill.className = 'dossier-bar-fill';
    barFill.style.width = `${(value / max) * 100}%`;
    barFill.style.background = color;
    barBg.appendChild(barFill);
    row.appendChild(barBg);

    const valueEl = document.createElement('span');
    valueEl.className = 'dossier-bar-value';
    valueEl.textContent = String(value);
    row.appendChild(valueEl);

    return row;
  }

  private getCountryEvents(
    events: NormalizedEvent[],
    countryCode: string,
    countryName: string,
  ): NormalizedEvent[] {
    const code = countryCode.toUpperCase();
    const name = countryName.toUpperCase();
    return events.filter((e) => {
      const cc = e.location?.countryCode?.toUpperCase();
      if (cc === code) return true;
      const cn = e.location?.country?.toUpperCase();
      if (cn === name) return true;
      return false;
    });
  }

  private getCountryConvergences(
    convergences: ConvergenceCell[],
    countryEvents: NormalizedEvent[],
  ): ConvergenceCell[] {
    if (countryEvents.length === 0 || convergences.length === 0) return [];

    // Build a set of approximate grid cells that the country's events cover
    const countryCells = new Set<string>();
    for (const ev of countryEvents) {
      if (ev.location?.lat != null && ev.location?.lng != null) {
        // 1-degree grid key (matches convergence detection)
        const key = `${Math.floor(ev.location.lat)},${Math.floor(ev.location.lng)}`;
        countryCells.add(key);
      }
    }

    return convergences.filter((cell) => {
      const key = `${Math.floor(cell.lat)},${Math.floor(cell.lng)}`;
      return countryCells.has(key);
    });
  }

  private getEventsCentroid(events: NormalizedEvent[]): { lat: number; lng: number } | null {
    let sumLat = 0;
    let sumLng = 0;
    let count = 0;

    for (const ev of events) {
      if (ev.location?.lat != null && ev.location?.lng != null) {
        sumLat += ev.location.lat;
        sumLng += ev.location.lng;
        count++;
      }
    }

    if (count === 0) return null;
    return { lat: sumLat / count, lng: sumLng / count };
  }
}

/** Factory: create the country dossier panel element. */
export function createCountryDossierPanel(): HTMLElement {
  const panel = new CountryDossierPanel();
  const el = panel.getElement();
  (el as HTMLElement & { destroy?: () => void }).destroy = () => panel.destroy();
  return el;
}
